import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, Grid, Stack, Tooltip, Typography } from '@mui/material';
import {
  Datagrid,
  FunctionField,
  List,
  useNotify,
  useRefresh,
  useTranslate,
} from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { paths } from 'src/routes/paths';
import ErrorPage403 from 'src/pages/error/403';
import {
  stErrorMessage,
  stOverview,
  stResolveAuditLog,
  StAuditLog,
  StOverview,
} from '../../../services/socialTradingService';
import { Resources } from '../../resource';
import {
  StActionButton,
  StActionRow,
  StEmpty,
  StIdCell,
  stFormatDateTime,
  stText,
  useStConfirmAction,
} from '../StListUi';
import './init';

const ACTION_KEYS: Record<string, string> = {
  SIGNAL_PARSE_FAILURE: 'module.socialTrading.actionSIGNAL_PARSE_FAILURE',
  REDPACKET_WITHDRAWAL_FAILURE: 'module.socialTrading.actionREDPACKET_WITHDRAWAL_FAILURE',
  USER_BAN: 'module.socialTrading.actionUSER_BAN',
  USER_UNBAN: 'module.socialTrading.actionUSER_UNBAN',
  CLAIM_THRESHOLD_UPDATE: 'module.socialTrading.actionCLAIM_THRESHOLD_UPDATE',
  POOL_CONFIGURE: 'module.socialTrading.actionPOOL_CONFIGURE',
  POOL_DISABLE: 'module.socialTrading.actionPOOL_DISABLE',
  ROLE_PERMISSION_UPDATE: 'module.socialTrading.actionROLE_PERMISSION_UPDATE',
  PRIVATE_CHAT_AUTH_UPDATE: 'module.socialTrading.actionPRIVATE_CHAT_AUTH_UPDATE',
};

const BLOCK_REASON_KEYS: Record<string, string> = {
  NONE: '',
  AWAITING_FINANCE_CONFIRM: 'module.socialTrading.todoAWAITING_FINANCE_CONFIRM',
  SETTLEMENT_QUEUED: 'module.socialTrading.todoSETTLEMENT_QUEUED',
  SETTLEMENT_FAILED: 'module.socialTrading.todoSETTLEMENT_FAILED',
};

function parseTodoObjectEntries(object: string): { key: string; value: string }[] {
  try {
    const o = JSON.parse(object || '{}') as Record<string, unknown>;
    if (!o || typeof o !== 'object' || Array.isArray(o)) return [];
    return Object.entries(o)
      .filter(([, v]) => v !== null && v !== undefined && v !== '')
      .map(([key, v]) => ({ key, value: String(v) }));
  } catch {
    return [];
  }
}

function shortReason(reason?: string | null): string {
  if (!reason) return '';
  return reason.length > 80 ? `${reason.slice(0, 80)}…` : reason;
}

export default function SocialTradingWorkbenchList() {
  const t = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const { askConfirm, confirmDialog } = useStConfirmAction();
  const [wb, setWb] = useState<StOverview | null>(null);
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setWb(await stOverview());
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  }, [notify]);

  useEffect(() => {
    load();
  }, [load]);

  const onResolve = async (id: string) => {
    setResolvingId(id);
    try {
      await stResolveAuditLog(id);
      notify(t('module.socialTrading.resolveOk'), { type: 'success' });
      refresh();
      await load();
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    } finally {
      setResolvingId(null);
    }
  };

  const claim = wb?.claimThreshold;
  const kpis = wb
    ? [
        {
          title: t('module.socialTrading.kpiClaim'),
          value: claim?.enabled ? claim.minClaimAmount : t('module.socialTrading.kpiClaimOff'),
          note: claim?.enabled
            ? `USDC · ${t('module.socialTrading.kpiClaimGlobal')}`
            : t('module.socialTrading.kpiClaimDisabled'),
        },
        {
          title: t('module.socialTrading.kpiAgents'),
          value: wb.agentsActive,
          note: `${t('module.socialTrading.kpiAgentsOn')} ${wb.agentsActive} · ${t('module.socialTrading.kpiAgentsOff')} ${wb.agentsInactive}`,
        },
        {
          title: t('module.socialTrading.kpiExceptions'),
          value: wb.exceptionsPending,
          note: `${t('module.socialTrading.kpiExceptionsSignal')} ${wb.exceptionsSignal} · ${t('module.socialTrading.kpiExceptionsRedPacket')} ${wb.exceptionsRedPacket}`,
        },
        {
          title: t('module.socialTrading.kpiPoolsPending'),
          value: wb.poolsPendingSettlement,
          note: BLOCK_REASON_KEYS[wb.settlementBlockReason]
            ? t(BLOCK_REASON_KEYS[wb.settlementBlockReason])
            : wb.settlementBlockReason || '—',
        },
      ]
    : [];

  return (
    <PermissionGuard resource="st-workbench" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent maxWidth={false}>
        <CustomBreadcrumbs
          heading={t('module.socialTrading.workbench')}
          links={[
            { name: t('module.socialTrading.title'), href: paths.socialTrading.workbench.root },
            { name: t('module.socialTrading.workbench') },
          ]}
          action={
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                load();
                refresh();
              }}
            >
              {t('module.socialTrading.refresh')}
            </Button>
          }
        />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
          {t('module.socialTrading.workbenchHint')}
        </Typography>

        <Stack spacing={2}>
          <Grid container spacing={2}>
            {kpis.map((k) => (
              <Grid item xs={12} sm={6} md={3} key={k.title}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      {k.title}
                    </Typography>
                    <Typography variant="h4">{k.value ?? '-'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {k.note}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Card sx={{ pb: 2 }}>
            <Box sx={{ px: 2, pt: 2, mb: 1 }}>
              <Typography variant="h6">{t('module.socialTrading.todos')}</Typography>
            </Box>
            <List
              resource={Resources.socialTradingWorkbenchTodos}
              actions={false}
              empty={false}
              perPage={20}
              exporter={false}
            >
              <Box sx={{ overflow: 'auto' }}>
                <Datagrid
                  rowClick={false}
                  bulkActionButtons={false}
                  empty={
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                      {t('module.socialTrading.emptyTodos')}
                    </Typography>
                  }
                >
                  <FunctionField
                    label={t('module.socialTrading.colType')}
                    render={(row: StAuditLog) =>
                      ACTION_KEYS[row.action] ? t(ACTION_KEYS[row.action]) : row.action
                    }
                  />
                  <FunctionField
                    label={t('module.socialTrading.colObjectType')}
                    render={(row: StAuditLog) => stText(row.objectType)}
                  />
                  <FunctionField
                    label={t('module.socialTrading.colObject')}
                    render={(row: StAuditLog) => {
                      const entries = parseTodoObjectEntries(row.object);
                      if (entries.length === 0) return <StEmpty />;
                      return (
                        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                          {entries.map(({ key, value }) => (
                            <Box
                              key={key}
                              display="flex"
                              alignItems="center"
                              gap={0.5}
                              sx={{ minWidth: 0 }}
                            >
                              <Typography
                                variant="body2"
                                component="span"
                                sx={{ fontSize: 12, color: 'text.secondary', flexShrink: 0 }}
                              >
                                {key}：
                              </Typography>
                              <StIdCell value={value} />
                            </Box>
                          ))}
                        </Stack>
                      );
                    }}
                  />
                  <FunctionField
                    label={t('module.socialTrading.colReason')}
                    render={(row: StAuditLog) => {
                      const full = row.reason || '';
                      if (!full) return <StEmpty />;
                      const short = shortReason(full);
                      return (
                        <Tooltip title={full}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 320 }}>
                            {short}
                          </Typography>
                        </Tooltip>
                      );
                    }}
                  />
                  <FunctionField
                    label={t('module.socialTrading.colTime')}
                    render={(row: StAuditLog) => stText(stFormatDateTime(row.createdAt))}
                  />
                  <FunctionField
                    label={t('module.socialTrading.colAction')}
                    textAlign="right"
                    render={(row: StAuditLog) =>
                      row.needsAction && !row.resolvedAt ? (
                        <StActionRow>
                          <StActionButton
                            disabled={resolvingId === row.id}
                            onClick={() =>
                              askConfirm({
                                title: t('module.socialTrading.confirmActionTitle'),
                                content: t('module.socialTrading.confirmActionBody', {
                                  action: t('module.socialTrading.resolve'),
                                }),
                                confirmLabel: t('module.socialTrading.confirmOk'),
                                onConfirm: () => onResolve(row.id),
                              })
                            }
                          >
                            {t('module.socialTrading.resolve')}
                          </StActionButton>
                        </StActionRow>
                      ) : (
                        stText(null)
                      )
                    }
                  />
                </Datagrid>
              </Box>
            </List>
          </Card>
        </Stack>

        {confirmDialog}
      </DashboardContent>
    </PermissionGuard>
  );
}
